import { useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import Alert from '@reach/alert';
import { Snackbar } from '@getsynapse/design-system';
import debounce from 'lodash/debounce';
import { useAppThunkDispatch } from 'state/store';
import {
  selectBulkActionsNotifications,
  selectBulkActionsNotificationsIds,
  resetBulkActionsNotifications,
  removeBulkActionNotification,
  selectBulkActionOperationId,
  undoBulkOperation,
  displayInProgressNotification,
} from 'state/ProjectTasksList/bulkActions/bulkActionsSlice';
import { setShouldRefreshTasks } from 'state/ProjectTasksList/actions/actionsSlice';
import useSnackbarNotifications from 'Hooks/useSnackbarNotifications/useSnackbarNotifications';

const SnackbarNotifications = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const dispatch = useAppThunkDispatch();
  const {
    notifications,
    showNotification,
    clearNotifications,
    hideNotification,
    notificationsIds: localNotificationsIds,
  } = useSnackbarNotifications();
  const bulkActionsNotifications = useSelector(selectBulkActionsNotifications);
  const bulkActionsNotificationsIds = useSelector(
    selectBulkActionsNotificationsIds
  );
  const bulkOperationId = useSelector(selectBulkActionOperationId);
  const handleUndoBulkOperationInProgress = useMemo(() => {
    return debounce(() => {
      dispatch(displayInProgressNotification());
    }, 3000);
  }, [dispatch]);

  const handleUndoBulkOperation = useCallback(async () => {
    clearNotifications();
    handleUndoBulkOperationInProgress();
    await dispatch(undoBulkOperation({ projectId }));
    dispatch(setShouldRefreshTasks(true));
  }, [
    dispatch,
    projectId,
    clearNotifications,
    handleUndoBulkOperationInProgress,
  ]);

  const handleCloseNotification = useCallback(
    (notificationId: string) => {
      dispatch(removeBulkActionNotification(notificationId));
      hideNotification(notificationId);
    },
    [dispatch, hideNotification]
  );

  useEffect(() => {
    const localIdsSet = new Set(localNotificationsIds);

    bulkActionsNotifications.forEach((notification) => {
      if (!localIdsSet.has(notification.id)) {
        showNotification({
          ...notification,
          onCloseCallback: () => {
            handleCloseNotification(notification.id);
          },
          ...(bulkOperationId && {
            actionButtonCallback: async () => {
              handleUndoBulkOperation();
            },
          }),
        });
      }
    });
  }, [
    bulkActionsNotifications,
    showNotification,
    handleUndoBulkOperation,
    bulkOperationId,
    localNotificationsIds,
    handleCloseNotification,
  ]);

  useEffect(() => {
    const reduxIdsSet = new Set(bulkActionsNotificationsIds);

    localNotificationsIds.forEach((id) => {
      if (!reduxIdsSet.has(id)) {
        hideNotification(id);
      }
    });
  }, [bulkActionsNotificationsIds, localNotificationsIds, hideNotification]);

  useEffect(() => {
    return () => {
      dispatch(resetBulkActionsNotifications());
      clearNotifications();
    };
  }, [dispatch, clearNotifications]);

  return (
    <div className='fixed left-6 bottom-12 z-75 flex flex-col gap-y-2'>
      {notifications.map((notification) => (
        <Alert key={notification.id} type='assertive'>
          <Snackbar {...notification} />
        </Alert>
      ))}
    </div>
  );
};

export default SnackbarNotifications;
